import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../../overmind';
import { useNavigate } from 'react-router';
import { SSOButton } from '../../sso-button/sso-button';
import { AccountPageLoaderLayer } from '../../../../components/general/loading-state/loader-layers/account-page-loader-layer/account-page-loader-layer';
import { UserRegistration } from '../../../../models/user';
import { FetchType } from '../../../../enums';
import { PagePath } from '../../../../navigation/navigation.enums';
import { Button, ConfirmationPasswordValidator, EmailValidator, FieldController, RequiredValidator, TextField, useMatchScreenWidth } from '@keplerco/core';
import { PasswordStrength } from '../../password-strength/password-strength';
import accountStyles from '../../account.module.css';
import { themedAssetUrl } from '../../../../library/helpers/themed-asset-url';
import classNames from 'classnames';
import { TertiaryButton } from '../../../../components/general/tertiary-button/tertiary-button';
import { NavLink } from 'react-router';

export function ClassicRegisterLayout(): JSX.Element {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const actions = useAppActions();
  const { samlUrl } = useAppState();

  const { control, handleSubmit, getValues, watch } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const isMobile = useMatchScreenWidth('mobile');

  const [fieldValues, setFieldValues] = useState<FieldValues>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  return (
    <div className={accountStyles.accountBackground} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className={accountStyles.accountPage}>
        <div className={classNames('card', accountStyles.accountCard)}>
          <AccountPageLoaderLayer path={PagePath.accountRegister}>
            {!isMobile && (
              <div className={accountStyles.accountNavigationToggler}>
                <span className="body">Already have an account?</span>
                {/* TODO: replace with Anchor once it's refactored to have NavLink functionality as well */}
                <TertiaryButton type="button" onClick={() => navigate(`${PagePath.accountBase}${PagePath.accountLogin}`)}>
                  Sign in
                </TertiaryButton>
              </div>
            )}
            <h1>Create an account</h1>
            <form
              id="register"
              className={accountStyles.accountForm}
              autoComplete="off"
              onSubmit={handleSubmit(async fieldValues => {
                actions.startLoader({ path: PagePath.accountRegister, type: FetchType.PageFetching });

                const result = await actions.registerUser(fieldValues as UserRegistration);

                if (result && result.message === 'An email has been sent to your address to confirm your account.') {
                  navigate(`${PagePath.accountBase}${PagePath.accountVerifyEmail}?email=${fieldValues.email}`);
                } else setErrorMessage(result?.message);

                actions.stopLoader(PagePath.accountRegister);
              })}
            >
              {!!errorMessage && (
                <div className="field">
                  <div style={{ padding: '0 0 20px 0' }}>
                    <div className="formErrorMessage">{errorMessage}</div>
                  </div>
                </div>
              )}

              <FieldController
                control={control}
                name="email"
                rules={new EmailValidator()}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="Email"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your email',
                    }}
                  />
                )}
              />

              <FieldController
                control={control}
                name="password"
                rules={new RequiredValidator('Enter your password')}
                render={({ field, fieldState }) => (
                  <div style={{ width: '100%', marginBottom: 12 }}>
                    <TextField
                      {...field}
                      type="password"
                      label="Password"
                      responsive
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: !!fieldState.error,
                        message: '',
                      }}
                    />

                    <PasswordStrength
                      password={fieldValues?.password}
                      onPercentage={percent => {
                        if (percent < 100) return;
                        actions.clearPasswordErrors();
                      }}
                    />
                  </div>
                )}
              />

              <FieldController
                control={control}
                name="confirmPassword"
                rules={new ConfirmationPasswordValidator('Enter your confirmation password', getValues('password'))}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Confirm Password"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your confirmation password',
                    }}
                  />
                )}
              />

              <span className="body" style={{ textAlign: 'start', marginBottom: 10 }}>
                By signing up, you agree to our <NavLink to={'https://legal.keplerco.io/documents/terms-of-use/'}>terms of service</NavLink> and <NavLink to={'https://legal.keplerco.io/documents/privacy-policy/'}>privacy policy</NavLink>
              </span>

              <Button chubby filled arrow={false} type="button">
                Register
              </Button>
            </form>

            <div className={accountStyles.accountDivider}>Or register using</div>

            {!!samlUrl && <SSOButton ssoType="saml" onClick={async () => (window.location.href = `${samlUrl}?returnUrl=${params.get('kt')},,,${params.get('code')}`)} />}

            <SSOButton ssoType="google" onClick={() => actions.externalRegister({ provider: 'Google' })} />

            <SSOButton ssoType="microsoft" onClick={() => actions.externalRegister({ provider: 'Microsoft' })} />

            <SSOButton ssoType="linkedin" onClick={() => actions.externalRegister({ provider: 'LinkedIn' })} />
          </AccountPageLoaderLayer>
        </div>
      </div>
    </div>
  );
}
